import { Box, Skeleton } from "@mui/material";
import { cardSx, type TonalTokens } from "../../../polish/tokens";
import { ResultsCardSkeleton } from "../feed/FeedSkeletons";

const NOTE_ROWS = 6;
const TARGET_ROWS = 4;
const GAP = { xs: 2, md: 3 } as const;
const CARD_PADDING = {
  px: { xs: 2, md: 3 },
  py: { xs: 2, md: 2.5 },
} as const;

interface SkeletonProps {
  tokens: TonalTokens;
  /** Locked: still grey, never shimmering. */
  still?: boolean;
}

interface RowsSkeletonProps extends SkeletonProps {
  rows: number;
  testId: string;
}

/** A titled card of rows: the price targets' or the notes' shape. */
const RowsSkeleton = ({
  tokens,
  still = false,
  rows,
  testId,
}: RowsSkeletonProps) => {
  const animation = still ? false : undefined;
  return (
    <Box data-testid={testId} sx={{ ...cardSx(tokens), ...CARD_PADDING }}>
      <Skeleton animation={animation} width="30%" height={26} sx={{ mb: 1 }} />
      {Array.from({ length: rows }, (_, at) => (
        <Skeleton key={at} animation={animation} height={44} />
      ))}
    </Box>
  );
};

/** The notes card's shape: a title, then six rows. */
export const NotesSkeleton = ({ tokens, still = false }: SkeletonProps) => (
  <RowsSkeleton
    tokens={tokens}
    still={still}
    rows={NOTE_ROWS}
    testId="notes-skeleton"
  />
);

/** The header card's shape: the company and the quarter, then the price and Since report. */
const HeaderSkeleton = ({ tokens }: SkeletonProps) => (
  <Box
    data-testid="report-header-skeleton"
    sx={{ ...cardSx(tokens), ...CARD_PADDING }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: 1 }}>
        <Skeleton
          variant="rounded"
          width={40}
          height={40}
          sx={{ borderRadius: "8px" }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="30%" height={22} />
          <Skeleton width="50%" height={18} />
        </Box>
      </Box>
      <Box>
        <Skeleton width={96} height={24} />
        <Skeleton width={80} height={18} sx={{ ml: "auto" }} />
      </Box>
    </Box>
    <Box sx={{ display: "flex", gap: 3, mt: { xs: 2.5, md: 3 } }}>
      <Box>
        <Skeleton width={40} height={16} />
        <Skeleton width={112} height={28} />
      </Box>
      <Box>
        <Skeleton width={76} height={16} />
        <Skeleton width={64} height={28} />
      </Box>
    </Box>
  </Box>
);

/** The page while it loads: the header beside the earlier reports, the results beside the targets, then the notes. */
export const ReportSkeleton = ({ tokens }: SkeletonProps) => (
  <Box
    data-testid="report-skeleton"
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        md: "repeat(2, minmax(0, 1fr))",
      },
      gap: GAP,
    }}
  >
    <HeaderSkeleton tokens={tokens} />
    <Box sx={{ display: { xs: "none", md: "grid" } }}>
      <RowsSkeleton tokens={tokens} rows={1} testId="previous-skeleton" />
    </Box>
    <ResultsCardSkeleton tokens={tokens} />
    <RowsSkeleton
      tokens={tokens}
      rows={TARGET_ROWS}
      testId="targets-skeleton"
    />
    <Box sx={{ gridColumn: "1 / -1" }}>
      <NotesSkeleton tokens={tokens} />
    </Box>
  </Box>
);
